/**
 * Centralized color-role registry for the design system.
 *
 * A **role** (`primary`, `destructive`, `white`, …) is a semantic intent, not a literal color. This
 * module is the single source of truth for what each role looks like, so two components can never
 * disagree about the same role name — the drift this replaces had `white` rendering as theme-fixed
 * white in `Badge` but as an *inverting* token in `Spinner` (near-black in dark mode, on exactly
 * the dark surfaces the role exists for), and `secondary` resolving to three different tokens.
 *
 * ⚠️ **Every class here must stay a complete, literal string.** Tailwind's scanner reads source
 * text, so a class assembled at runtime (`` `bg-${role}` ``) is invisible to it and emits no CSS at
 * all. This is the same constraint {@link Z_INDEX} documents — the maps below are written longhand
 * for exactly that reason. Never derive one entry from another by string concatenation of the
 * *utility* part; composing whole literal strings together is fine.
 *
 * ## Shape
 *
 * Roles are grouped by the shape of component that consumes them, because the three consumers are
 * not the same kind of thing:
 *
 * - {@link FILL_ROLES} — components that paint a surface and/or a border: `Button` (solid, soft,
 *   outline, ghost, link) and `Badge` (solid, soft, outline, dashed). Each entry holds the *tokens*
 *   for a form factor, not a finished class list, so each component can apply its own hover gating: `Badge`
 *   gates hovers behind `[a&]:` (they only apply when it renders as a link), `Button` does not.
 * - {@link RING_ROLES} — `Spinner`, which colors a spinning ring border, a track behind it, and an
 *   optional label. It is deliberately NOT forced into the fill vocabulary; its
 *   `primary`/`secondary`/`muted` are an opacity emphasis ramp off `--foreground`, which is a
 *   different (and intentional) meaning from Button's fills.
 *
 * A component picks the subset of roles it offers and derives its prop union from that subset, so
 * the `cva` keys and the public union cannot drift apart.
 */

/**
 * The roles every surface component offers. A component's own role list extends this rather than
 * restating it, so a role added here reaches all of them at once:
 *
 * ```ts
 * export const BADGE_ROLES = [...BASE_ROLES, "success", "warning", "info"] as const;
 * ```
 */
export const BASE_ROLES = ["primary", "secondary", "destructive", "muted", "white"] as const;

/** Every role name in the system. Individual components offer a subset — see the `*_ROLES` lists. */
export type ColorRole = (typeof BASE_ROLES)[number] | "success" | "warning" | "info" | "ai";

/**
 * One role's tokens for a surface-painting component.
 *
 * Split into parts rather than one finished string so each consumer composes its own hover gating
 * (`Badge` needs `[a&]:` prefixes, `Button` does not) without the registry carrying two
 * near-identical copies of every role.
 */
export interface FillRoleTokens {
  /** Filled treatment: background + on-color ink, e.g. `"bg-primary text-primary-foreground"`. */
  readonly solidBase: string;
  /** The filled treatment's hover background, unprefixed, e.g. `"hover:bg-primary/90"`. */
  readonly solidHover: string;
  /**
   * Tinted treatment: a quiet always-visible fill + the role's **own saturated ink**, e.g.
   * `"bg-primary/10 text-primary"`. Sits between `solidBase` (saturated fill, on-color ink) and
   * `outlineBase` (no fill until hover) in emphasis. The ink is deliberately not the
   * `-foreground` on-color token: this fill is light enough that on-color ink would be unreadable.
   */
  readonly softBase: string;
  /** The tinted treatment's hover background, unprefixed, e.g. `"hover:bg-primary/15"`. */
  readonly softHover: string;
  /** Bordered/transparent treatment: border color + ink. Empty where the component's own variant supplies both. */
  readonly outlineBase: string;
  /** The bordered treatment's hover, unprefixed. */
  readonly outlineHover: string;
}

/**
 * Surface roles for `Button` and `Badge`.
 *
 * `white` is the one theme-fixed role: literal white in both light and dark mode, for elements on a
 * dark, saturated, or photographic surface. It deliberately does not invert, so its ink is a
 * hardcoded neutral rather than a `--*-foreground` token — every such token flips with the theme
 * and would render near-white on white (1.02:1) in one of the two modes.
 *
 * `success`/`warning`/`info` follow the same contract as every other role: the base token is the
 * saturated fill and `-foreground` is the ink that sits on it, so the bordered treatment tints its
 * border and text with the fill token itself.
 */
/**
 * Soft fills are an alpha step off each role's own fill token (`/10` at rest, `/15` on hover) with
 * the role's saturated color as ink. The alpha is the point: a soft button composites onto whatever
 * surface it sits on — a card, a colored callout — rather than replacing it with a flat patch.
 *
 * `secondary` and `muted` are the exceptions, and they are unavoidable: both are already *surface*
 * tokens (90% and 96.1% lightness), so a 10% veil of one over a light page is a fraction of a
 * percent of contrast — invisible. They take their full opaque fill instead, which is also why a
 * soft button on a tinted surface should prefer a chromatic role. `warning` takes a heavier /20
 * (a 10% veil of an already-pale yellow doesn't separate from the page) and writes in
 * `--warning-ink`; `white` stays theme-fixed for the dark backdrops that role exists for.
 */
export const FILL_ROLES = {
  primary: {
    solidBase: "bg-primary text-primary-foreground",
    solidHover: "hover:bg-primary/90",
    softBase: "bg-primary/10 text-primary",
    softHover: "hover:bg-primary/15",
    outlineBase: "border-primary/40 text-primary",
    // Keeps the primary ink through the hover. Swapping to `accent-foreground` (the previous
    // behavior) drops a branded primary back to neutral grey exactly when it is being interacted
    // with, which reads as the color being lost rather than the surface lifting.
    outlineHover: "hover:bg-accent hover:text-primary",
  },
  secondary: {
    solidBase: "bg-secondary text-secondary-foreground",
    solidHover: "hover:bg-secondary/80",
    // `--secondary` is itself a pale surface token, so a /10 of it is invisible against the page.
    // Soft secondary takes the full fill and leans on its own dark ink for the contrast instead --
    // the one role whose soft and solid treatments nearly coincide.
    softBase: "bg-secondary text-secondary-foreground",
    softHover: "hover:bg-secondary/80",
    outlineBase: "text-secondary-foreground",
    // Holds its own ink through the hover, same reasoning as `primary` above.
    outlineHover: "hover:bg-accent hover:text-secondary-foreground",
  },
  destructive: {
    solidBase:
      "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
    solidHover: "hover:bg-destructive/90",
    softBase: "bg-destructive/10 text-destructive",
    softHover: "hover:bg-destructive/15",
    outlineBase: "border-destructive/40 text-destructive",
    outlineHover: "hover:bg-destructive/10",
  },
  muted: {
    solidBase: "bg-muted text-muted-foreground",
    solidHover: "hover:bg-muted/90",
    // Same as `secondary`: `--muted` is a surface token, so a /10 of it reads as nothing. Takes the
    // full fill with the muted ink.
    softBase: "bg-muted text-muted-foreground",
    softHover: "hover:bg-muted/80",
    outlineBase: "text-muted-foreground",
    outlineHover: "hover:bg-muted",
  },
  white: {
    solidBase: "bg-white text-neutral-900",
    solidHover: "hover:bg-white/90",
    // Theme-fixed like the rest of the role: a translucent white scrim for dark/photographic
    // backdrops, writing in literal white rather than an inverting `-foreground` token.
    softBase: "bg-white/15 text-white",
    softHover: "hover:bg-white/25",
    outlineBase: "border-white/40 text-white",
    outlineHover: "hover:bg-white/10",
  },
  success: {
    solidBase: "bg-success text-success-foreground",
    solidHover: "hover:bg-success/90",
    softBase: "bg-success/10 text-success",
    softHover: "hover:bg-success/15",
    outlineBase: "border-success/40 text-success",
    outlineHover: "hover:bg-success/10",
  },
  // The one role whose fill is light enough to need dark ink, and whose on-page ink is a separate
  // token: `--warning` is a true yellow (1.5:1 on a white page), so the fill-less variants write in
  // `--warning-ink` instead of the fill itself.
  warning: {
    // The fill is only ~1.5:1 against a white page, so it takes a faint same-hue border to keep its
    // edge from disappearing — every variant already reserves a transparent border box, so this
    // costs no layout shift.
    solidBase: "bg-warning text-warning-foreground border-warning-ink/25",
    solidHover: "hover:bg-warning/90",
    // Writes in `--warning-ink` for the same reason the fill-less treatments do: `--warning` at
    // 1.5:1 is a fill, not an ink. The tint itself is a heavier /20 than the other roles' /10 --
    // a /10 of an already-pale yellow doesn't separate from the page.
    softBase: "bg-warning/20 text-warning-ink",
    softHover: "hover:bg-warning/30",
    // Neutral border, not a tinted one: a yellow border at this lightness reads as a stray
    // highlight rather than an edge, so warning follows `secondary`/`muted` and lets its ink carry
    // the intent.
    outlineBase: "border-border text-warning-ink",
    outlineHover: "hover:bg-warning/20",
  },
  info: {
    solidBase: "bg-info text-info-foreground",
    solidHover: "hover:bg-info/90",
    softBase: "bg-info/10 text-info",
    softHover: "hover:bg-info/15",
    outlineBase: "border-info/40 text-info",
    outlineHover: "hover:bg-info/10",
  },
  // The one saturated hue that holds the same value in both themes, so its solid ink is a literal
  // white (via --ai-foreground) rather than a token that inverts.
  ai: {
    solidBase: "bg-ai text-ai-foreground",
    solidHover: "hover:bg-ai/90",
    // An alpha of `--ai` rather than the opaque `--ai-muted` token, so a soft ai button composites
    // onto whatever is behind it -- notably the `--ai-background` confirmation bubble, where an
    // opaque tint would sit on the surface as a flat patch instead of deepening it. `--ai` holds
    // the same value in both themes, so one alpha pair covers light and dark.
    //
    // Ink stays `--foreground`, unlike every other role's soft treatment: this tint reads as a
    // panel the label sits on rather than a colored control, so violet-on-violet would make the
    // label the loudest thing in a bubble that is meant to be quiet.
    softBase: "bg-ai/10 text-foreground",
    softHover: "hover:bg-ai/15",
    outlineBase: "border-ai/40 text-ai",
    outlineHover: "hover:bg-ai/10",
  },
} as const satisfies Partial<Record<ColorRole, FillRoleTokens>>;

/**
 * The `[a&]:`-gated hover for each role, used by components whose hover applies only when they
 * render as a link (`Badge`).
 *
 * These are spelled out longhand rather than derived from {@link FILL_ROLES}' `*Hover` by
 * prefixing: `[a&]:hover:bg-muted` is an **arbitrary variant**, so Tailwind only emits it if that
 * exact full string appears in scanned source. Building it at runtime would silently produce no
 * CSS — the same trap {@link Z_INDEX} documents. Keep each string in sync with its unprefixed
 * counterpart above.
 */
export const FILL_ROLE_LINK_HOVERS = {
  primary: {
    solid: "[a&]:hover:bg-primary/90",
    soft: "[a&]:hover:bg-primary/15",
    outline: "[a&]:hover:bg-accent [a&]:hover:text-primary",
  },
  secondary: {
    solid: "[a&]:hover:bg-secondary/80",
    soft: "[a&]:hover:bg-secondary/80",
    outline: "[a&]:hover:bg-accent [a&]:hover:text-secondary-foreground",
  },
  destructive: {
    solid: "[a&]:hover:bg-destructive/90",
    soft: "[a&]:hover:bg-destructive/15",
    outline: "[a&]:hover:bg-destructive/10",
  },
  muted: {
    solid: "[a&]:hover:bg-muted/90",
    soft: "[a&]:hover:bg-muted/80",
    outline: "[a&]:hover:bg-muted",
  },
  white: {
    solid: "[a&]:hover:bg-white/90",
    soft: "[a&]:hover:bg-white/25",
    outline: "[a&]:hover:bg-white/10",
  },
  success: {
    solid: "[a&]:hover:bg-success/90",
    soft: "[a&]:hover:bg-success/15",
    outline: "[a&]:hover:bg-success/10",
  },
  warning: {
    solid: "[a&]:hover:bg-warning/90",
    soft: "[a&]:hover:bg-warning/30",
    outline: "[a&]:hover:bg-warning/10",
  },
  info: {
    solid: "[a&]:hover:bg-info/90",
    soft: "[a&]:hover:bg-info/15",
    outline: "[a&]:hover:bg-info/10",
  },
  ai: {
    solid: "[a&]:hover:bg-ai/90",
    soft: "[a&]:hover:bg-ai/15",
    outline: "[a&]:hover:bg-ai/10",
  },
} as const satisfies Partial<Record<ColorRole, { solid: string; soft: string; outline: string }>>;

/** One role's tones for `Spinner`: the spinning arc, the faint track behind it, and the label. */
export interface RingRoleTokens {
  /** The spinning arc's border color. */
  readonly ring: string;
  /** The faint full ring behind the arc, so the spinner reads as a ring rather than a lone sliver. */
  readonly track: string;
  /** The optional label's text color. */
  readonly label: string;
}

/**
 * Ring roles for `Spinner`.
 *
 * `primary` and `secondary` take their **color** tokens, so both track a branded palette the way a
 * solid Button does. `muted` takes `--muted-foreground` rather than `--muted`, which at 1.04:1
 * against a white page is a fill with no contrast of its own and renders as no ring at all.
 *
 * Labels are toned for *readability*, not to match their ring: `--secondary-foreground` is
 * near-black and would caption a pale ring with heavy text, so `secondary` and `muted` both label
 * in the neutral muted ink.
 *
 * The contrast to know: `--secondary` is a surface token, so as a 4px stroke it is 1.11:1 on a
 * white page — visibly tinted but deliberately soft, and well under the 3:1 an interactive control
 * would need. That is fine for a status indicator; reach for `primary` when the spinner has to be
 * noticed.
 *
 * The track is `--muted` for every role except `white`, which needs a translucent white track for
 * the dark surfaces it sits on.
 */
export const RING_ROLES = {
  primary: { ring: "border-primary", track: "border-muted border-t-muted", label: "text-primary" },
  // Ring takes the `--secondary` color so the role is visibly itself (and tracks a branded
  // palette); the label takes the neutral muted ink rather than `--secondary-foreground`, which is
  // near-black and would read far heavier than the ring it captions.
  secondary: { ring: "border-secondary", track: "border-muted border-t-muted", label: "text-muted-foreground" },
  destructive: { ring: "border-destructive", track: "border-muted border-t-muted", label: "text-destructive" },
  // The faintest role that is still a visible ring: `--muted-foreground` is 2.14:1 on a white page,
  // and stepping it down any further (a /60 lands at 1.41:1 against its own track) is the point
  // where the arc stops reading as a ring at all.
  muted: { ring: "border-muted-foreground", track: "border-muted border-t-muted", label: "text-muted-foreground" },
  white: { ring: "border-white", track: "border-white/40 border-t-white/40", label: "text-white" },
  ai: { ring: "border-ai", track: "border-muted border-t-muted", label: "text-ai" },
} as const satisfies Partial<Record<ColorRole, RingRoleTokens>>;
